import { styled } from '@material-ui/styles'
import React, { MouseEvent, PropsWithChildren, useEffect, useRef } from 'react'
import { GridCellWidth } from 'core/elements/grid/hooks/useGridRows'
import Theme from 'core/themes/model'
import { switchCase } from 'utils/fp'

const FALLBACK_MIN_WIDTH = 60

// Drag the handle to resize the column. Widths are written straight to the DOM (no React state)
// so a drag does not re-render the grid on every mousemove. The table is `table-layout: fixed`
// but has no explicit width, so the browser falls back to auto layout and ignores a single
// column's width. Pinning every header width + the table width makes the fixed layout apply.
// Fix every header cell at its current width (or its saved one, by `data-column-key`) and size
// the table to their sum.
export const pinColumnWidths = (
  table: HTMLTableElement,
  headers: HTMLElement[],
  saved: Record<string, number> = {},
) => {
  const widths = headers.map((h) => saved[h.dataset.columnKey] ?? h.getBoundingClientRect().width)
  headers.forEach((h, i) => {
    // widths are border-box (getBoundingClientRect); th/td default to content-box, which would
    // add the cell padding on top and widen every column (the narrow checkbox one most)
    h.style.boxSizing = 'border-box'
    h.style.width = `${widths[i]}px`
  })
  const total = widths.reduce((sum, w) => sum + w, 0)
  table.style.width = `${total}px`
  return total
}

const startResize = (
  e: MouseEvent<HTMLSpanElement>,
  onResizeEnd?: (widths: Record<string, number>) => void,
): (() => void) => {
  e.preventDefault()
  e.stopPropagation()
  const th = e.currentTarget.parentElement
  const table = th.closest('table')
  const headers = Array.from(th.parentElement.children) as HTMLElement[]
  const startTableWidth = pinColumnWidths(table, headers)
  const startX = e.clientX
  const startWidth = th.getBoundingClientRect().width
  // use the column's existing min width, 60px if it has none
  const minWidth = parseFloat(getComputedStyle(th).minWidth) || FALLBACK_MIN_WIDTH
  const onMove = (ev: globalThis.MouseEvent) => {
    const newWidth = Math.max(minWidth, startWidth + ev.clientX - startX)
    th.style.width = `${newWidth}px`
    table.style.width = `${startTableWidth + newWidth - startWidth}px`
  }
  const onUp = () => {
    document.removeEventListener('mousemove', onMove)
    document.removeEventListener('mouseup', onUp)
    document.body.style.userSelect = ''
    onResizeEnd?.(
      Object.fromEntries(
        headers
          .filter((h) => h.dataset.columnKey)
          .map((h) => [h.dataset.columnKey, h.getBoundingClientRect().width]),
      ),
    )
    // The click that follows mouseup lands on the <th> (not the handle) when the pointer moved,
    // which would toggle sort. Swallow it. Any click is preceded by a mousedown, so the next
    // mousedown drops the guard if no click followed this drag.
    const swallowClick = (ev: globalThis.MouseEvent) => ev.stopPropagation()
    const dropGuard = () => document.removeEventListener('click', swallowClick, true)
    document.addEventListener('click', swallowClick, {
      capture: true,
      once: true,
    })
    document.addEventListener('mousedown', dropGuard, {
      capture: true,
      once: true,
    })
  }
  document.body.style.userSelect = 'none'
  document.addEventListener('mousemove', onMove)
  document.addEventListener('mouseup', onUp)
  // abort a drag in progress (heading unmounted mid-drag): no click guard needed
  return () => {
    document.removeEventListener('mousemove', onMove)
    document.removeEventListener('mouseup', onUp)
    document.body.style.userSelect = ''
  }
}

interface GridTableHeadingProps {
  sortingDisabled: boolean
  width: GridCellWidth
  resizable?: boolean
  onResizeEnd?: (widths: Record<string, number>) => void
}

export default styled(({ sortingDisabled, width, resizable, onResizeEnd, children, ...props }) => {
  const abortResize = useRef<() => void>()
  useEffect(() => () => abortResize.current?.(), [])
  return (
    <th {...props}>
      {children}
      {resizable && (
        <span
          className="grid_header-resizer"
          data-testid="grid-header--resizer"
          onMouseDown={(e) => {
            abortResize.current = startResize(e, onResizeEnd)
          }}
          onClick={(e) => e.stopPropagation()}
        />
      )}
    </th>
  )
})<Theme, PropsWithChildren<GridTableHeadingProps>>(
  ({ theme, width = 'small', sortingDisabled }) => ({
    position: 'relative',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    border: 0,
    margin: 0,
    minWidth: switchCase(
      {
        small: 80,
        medium: 160,
        large: 340,
      },
      width,
    )(width),
    padding: theme.spacing(0.5, 1),
    cursor: sortingDisabled ? 'default' : 'pointer',
    color: theme.components.table.headColor,
    // box-shadow, not border: collapsed borders don't follow the sticky header
    '&:not(:first-child):not(.row-menu)': {
      boxShadow: `inset 1px 0 0 ${theme.components.table.border}`,
    },
    '&:first-child': {
      paddingLeft: 16,
    },
    '&:last-child': {
      paddingRight: 16,
    },
    '&.select-column': {
      width: 26,
      minWidth: 26,
      '&:after': {
        content: 'none',
      },
      '& > .checkbox': {
        padding: 4,
        marginLeft: -4,
        width: 16,
      },
    },
    '& > .grid_header-resizer': {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      width: 6,
      cursor: 'col-resize',
      zIndex: 1,
      '&:hover': {
        backgroundColor: theme.components.table.border,
      },
    },
    '& > .grid_header-text': {
      color: theme.components.table.headColor,
    },
    '& > .grid_header-direction': {
      position: 'absolute',
      right: 0,
      top: '50%',
      fontSize: 12,
      transform: 'translate(0, -50%)',
      padding: theme.spacing(0, 0.5),
      '&:before': {
        height: 6,
        display: 'block',
      },
    },
  }),
)
